'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/shadcn/dialog';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { useState } from 'react';

type DialogSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl';

interface ToastConfig {
  title: string;
  description?: string;
}

interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  /** External submitting state. If not provided, component manages its own state when using toast. */
  submitting?: boolean;
  title?: string;
  description?: string;
  confirmText?: string;
  confirmingText?: string;
  cancelText?: string;
  size?: DialogSize;
  /** Toast to show on successful deletion. If provided, component handles the async flow. */
  successToast?: ToastConfig;
  /** Toast to show on error. Defaults to generic error message if successToast is provided. */
  errorToast?: ToastConfig;
  /** Called after successful deletion (after toast). */
  onSuccess?: () => void;
}

export function DeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  submitting: externalSubmitting,
  title = 'Delete',
  description = 'Are you sure you want to delete this item? This action cannot be undone.',
  confirmText = 'Delete',
  confirmingText = 'Deleting...',
  cancelText = 'Cancel',
  size = 'sm',
  successToast,
  errorToast,
  onSuccess
}: DeleteConfirmDialogProps) {
  const { toast } = useToast();
  const [internalSubmitting, setInternalSubmitting] = useState(false);

  const isManaged = successToast !== undefined;
  const submitting = isManaged
    ? internalSubmitting
    : (externalSubmitting ?? false);

  const handleConfirm = async () => {
    if (!isManaged) {
      await onConfirm();
      return;
    }

    setInternalSubmitting(true);
    try {
      await onConfirm();
      toast({
        title: successToast.title,
        description: successToast.description,
        variant: 'default'
      });
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: errorToast?.title ?? 'Error deleting',
        description:
          errorToast?.description ??
          (error instanceof Error
            ? error.message
            : 'An unknown error occurred'),
        variant: 'destructive'
      });
    } finally {
      setInternalSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className={size !== 'lg' ? `sm:max-w-${size}` : undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            onClick={onClose}
            disabled={submitting}
            size="sm"
            variant="outline"
          >
            {cancelText}
          </Button>
          <Button
            color="red"
            onClick={handleConfirm}
            disabled={submitting}
            size="sm"
          >
            {submitting ? confirmingText : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
